import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    Chip,
    NumberInput,
    Button,
} from 'minimal-ui';
import ComingSoonImage from '../../../../assets/img/coming-soon.png';

const ProductCard = ({ 
    product, 
    onQuantityChange,
    onQuantityLimitExceeded,
    quantity, 
    maxQuantityFromStock = null,
    stockQty = null,
    tabType, 
    pricingType, 
    setPricingType, 
    onAddToCart, 
    existingCartQuantity, 
    hasOtherPromotionsInCart,
    compulsoryMinQuantity = null,
}) => {
    const productType = product?.product_type || 'Box';
    const showToggle = productType === 'Both';

    const [imageError, setImageError] = useState(false);
    
    const isCompulsory =
      product?.is_compulsory === "1" ||
      product?.is_compulsory === 1 ||
      product?.is_compulsory === true;
    const perSetCompulsoryQty = parseInt(product?.compulsory_quantity || 0, 10);
    const compulsoryQty =
      tabType === "get" &&
      compulsoryMinQuantity != null &&
      Number(compulsoryMinQuantity) > 0
        ? Number(compulsoryMinQuantity)
        : perSetCompulsoryQty;
    const maxQuantity =
        maxQuantityFromStock != null && Number.isFinite(maxQuantityFromStock)
            ? maxQuantityFromStock
            : 9999;
    const qtyNum = typeof quantity === 'number' && Number.isFinite(quantity) ? quantity : 0;

    const [localQty, setLocalQty] = useState(qtyNum);
    const localQtyRef = useRef(qtyNum);
    const lastAttemptedQtyRef = useRef(qtyNum);
    const suppressLimitDialogRef = useRef(false);
    const inputFocusedRef = useRef(false);

    const syncLocalQty = useCallback((nextQty) => {
        const safeQty = Math.min(Math.max(nextQty, 0), maxQuantity);
        localQtyRef.current = safeQty;
        setLocalQty(safeQty);
    }, [maxQuantity]);

    useEffect(() => {
        if (!inputFocusedRef.current) {
            syncLocalQty(qtyNum);
        }
    }, [qtyNum, syncLocalQty]);

    useEffect(() => {
        if (inputFocusedRef.current && localQtyRef.current > maxQuantity) {
            syncLocalQty(maxQuantity);
        }
    }, [maxQuantity, syncLocalQty]);
    const useIncrementBlockedHandler =
        maxQuantityFromStock != null &&
        Number.isFinite(maxQuantityFromStock) &&
        qtyNum >= maxQuantity;

    const commitQuantityToParent = useCallback(
        (rawValue, suppressLimitDialog = false) => {
            let numValue = typeof rawValue === 'number' ? rawValue : parseInt(rawValue, 10) || 0;
            if (!Number.isFinite(numValue)) numValue = 0;
            if (tabType === 'get' && isCompulsory && compulsoryQty > 0 && numValue > 0 && numValue < compulsoryQty) {
                return;
            }
            const accepted = onQuantityChange(
                product?.product_id,
                numValue,
                tabType,
                pricingType,
                { suppressLimitDialog }
            );
            if (accepted === false) {
                syncLocalQty(qtyNum);
            } else {
                syncLocalQty(Math.min(Math.max(numValue, 0), maxQuantity));
            }
        },
        [
            maxQuantity,
            qtyNum,
            onQuantityChange,
            product?.product_id,
            tabType,
            pricingType,
            isCompulsory,
            compulsoryQty,
            syncLocalQty,
        ]
    );

    const flushInputQuantity = useCallback(() => {
        inputFocusedRef.current = false;
        let attempted = lastAttemptedQtyRef.current;
        if (!Number.isFinite(attempted)) attempted = localQtyRef.current;
        if (!Number.isFinite(attempted)) attempted = qtyNum;
        if (tabType === 'get' && isCompulsory && compulsoryQty > 0 && attempted === 0 && qtyNum > 0) {
            attempted = Math.max(qtyNum, compulsoryQty);
        }
        if (tabType === 'get' && isCompulsory && compulsoryQty > 0 && attempted > 0 && attempted < compulsoryQty) {
            attempted = compulsoryQty;
        }
        const suppressLimitDialog = suppressLimitDialogRef.current;
        suppressLimitDialogRef.current = false;
        commitQuantityToParent(attempted, suppressLimitDialog);
    }, [
        qtyNum,
        tabType,
        isCompulsory,
        compulsoryQty,
        commitQuantityToParent,
    ]);

    const getPrices = () => {
        let salesPrice, offerPrice;

        if (pricingType === 'Box') {
            salesPrice = parseFloat( product?.sales_price || 0);
        } else {
            salesPrice = parseFloat( product?.sales_price || 0);
        }

        return { salesPrice, offerPrice };
    };

    const { salesPrice, offerPrice } = getPrices();

    const displayPrice = offerPrice > 0 ? offerPrice : salesPrice;
    const hasDiscount = offerPrice > 0 && offerPrice < salesPrice;

    return (
        <Card sx={{
            px: 2,
            py: 1,
            borderRadius: '10px',
            mb: 1,
            border: '1px solid rgba(202, 200, 200, 0.85)'
        }}>
            <Box display="flex" gap={2} alignItems="flex-start">
                <Box
                    component="img"
                    src={product?.image_url && !imageError ? product.image_url : ComingSoonImage}
                    alt={product?.product_name || 'Product'}
                    onError={() => setImageError(true)}
                    sx={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '7px',
                        objectFit: 'cover',
                        flexShrink: 0
                    }}
                />

                <Box flex={1}>
                    <Typography variant="body1" fontWeight={500} mb={0.5}>
                        {product?.product_name || 'N/A'}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        {showToggle ? (
                            <Box display="flex" gap={1}>
                                <Chip
                                    onClick={() => setPricingType('Box')}
                                    sx={{
                                        cursor: 'pointer',
                                        '&.MuiChip-root': {
                                            border: '0.5px solid',
                                            borderColor: pricingType === 'Box' ? 'error.main' : '#E0E0E0',
                                            borderRadius: '3px',
                                            width: '50px',
                                            color: pricingType === 'Box' ? 'error.main' : 'text.primary',
                                            ...(pricingType === 'Box' ? {} : { backgroundColor: 'white' }),
                                            fontSize: '12px',
                                            height: '24px'
                                        }
                                    }}
                                    color={pricingType === 'Box' ? 'error' : 'default'}
                                    label="Box"
                                    variant="soft"
                                    size="small"
                                />
                                <Chip
                                    onClick={() => setPricingType('Piece')}
                                    sx={{
                                        cursor: 'pointer',
                                        '&.MuiChip-root': {
                                            border: '0.5px solid',
                                            borderColor: pricingType === 'Piece' ? 'error.main' : '#E0E0E0',
                                            borderRadius: '3px',
                                            width: '50px',
                                            color: pricingType === 'Piece' ? 'error.main' : 'text.primary',
                                            ...(pricingType === 'Piece' ? {} : { backgroundColor: 'white' }),
                                            fontSize: '12px',
                                            height: '24px'
                                        }
                                    }}
                                    color={pricingType === 'Piece' ? 'error' : 'default'}
                                    label="Piece"
                                    variant="soft"
                                    size="small"
                                />
                            </Box>
                        ) : (
                            <Chip
                                sx={{
                                    '&.MuiChip-root': {
                                        border: '0.5px solid',
                                        borderColor: 'error.main',
                                        borderRadius: '3px',
                                        width: '50px',
                                        color: 'error.main',
                                        fontSize: '12px',
                                        height: '24px'
                                    }
                                }}
                                variant="soft"
                                label={productType}
                                color="error"
                                size="small"
                            />
                        )}
                        <Typography variant="body2" color="#212b36" fontSize="12px" sx={{ whiteSpace: "nowrap" }}>
                            Size: {product?.pack || 'N/A'}x{product?.split || 'N/A'}g
                        </Typography>
                        <Typography variant="body2" color="#212b36" fontSize="12px">
                            |
                        </Typography>
                        <Typography variant="body2" color="#212b36" fontSize="12px" sx={{ whiteSpace: "nowrap" }}>
                            Code: {product?.product_code || 'N/A'}
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        {hasDiscount && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textDecoration: 'line-through' }}
                            >
                                £{salesPrice.toFixed(2)}
                            </Typography>
                        )}
                        <Typography variant="body2" fontWeight={600}>
                            {displayPrice === 0 ? 'Free' : `£${displayPrice.toFixed(2)}`}
                        </Typography>
                    </Box>


                </Box>

                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                    <NumberInput
                        key={`${tabType}_${product?.product_id}_${pricingType}`}
                        sx={{ width: '100px' }}
                        value={localQty}
                        onChange={(event, value, attemptedRaw) => {
                            const numValue = typeof value === 'number' ? value : parseInt(value, 10) || 0;
                            const attempted =
                                typeof attemptedRaw === 'number' && Number.isFinite(attemptedRaw)
                                    ? attemptedRaw
                                    : numValue;
                            lastAttemptedQtyRef.current = attempted;
                            const cappedQty = Math.min(Math.max(numValue, 0), maxQuantity);
                            if (event.type === 'click') {
                                if (
                                    tabType === 'get' &&
                                    isCompulsory &&
                                    compulsoryQty > 0 &&
                                    attempted > 0 &&
                                    attempted < compulsoryQty
                                ) {
                                    return;
                                }
                                const accepted = onQuantityChange(
                                    product?.product_id,
                                    attempted,
                                    tabType,
                                    pricingType
                                );
                                if (accepted !== false) {
                                    syncLocalQty(cappedQty);
                                }
                                return;
                            }
                            if (
                                tabType === 'get' &&
                                isCompulsory &&
                                compulsoryQty > 0 &&
                                cappedQty === 0 &&
                                qtyNum > 0
                            ) {
                                return;
                            }
                            if (
                                tabType === 'get' &&
                                isCompulsory &&
                                compulsoryQty > 0 &&
                                cappedQty > 0 &&
                                cappedQty < compulsoryQty
                            ) {
                                return;
                            }
                            syncLocalQty(cappedQty);
                            if (
                                maxQuantityFromStock != null &&
                                Number.isFinite(maxQuantityFromStock) &&
                                attempted > maxQuantity
                            ) {
                                onQuantityLimitExceeded?.(
                                    product?.product_id,
                                    attempted,
                                    tabType,
                                    pricingType
                                );
                                suppressLimitDialogRef.current = true;
                            }
                        }}
                        min={
                            tabType === 'get' && isCompulsory && compulsoryQty > 0 && qtyNum > 0
                                ? compulsoryQty
                                : 0
                        }
                        max={maxQuantity}
                        disableInput={maxQuantityFromStock === 0}
                        onIncrementBlocked={
                            useIncrementBlockedHandler
                                ? () => {
                                      const attempted = maxQuantity + 1;
                                      lastAttemptedQtyRef.current = attempted;
                                      onQuantityChange(
                                          product?.product_id,
                                          attempted,
                                          tabType,
                                          pricingType
                                      );
                                  }
                                : undefined
                        }
                        slotProps={{
                            ...(useIncrementBlockedHandler
                                ? {
                                      incrementButton: {
                                          sx: {
                                              opacity: 0.45,
                                              cursor: 'not-allowed',
                                          },
                                      },
                                  }
                                : {}),
                            input: {
                                onFocus: () => {
                                    inputFocusedRef.current = true;
                                    lastAttemptedQtyRef.current = localQtyRef.current;
                                    suppressLimitDialogRef.current = false;
                                },
                                onBlur: () => {
                                    flushInputQuantity();
                                },
                                onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.currentTarget.blur();
                                    }
                                },
                            },
                        }}
                    />
                    {stockQty === 0 || maxQuantityFromStock === 0 ? (
                        <Typography
                            variant="caption"
                            color="error"
                            fontSize="11px"
                            sx={{ textAlign: 'right', width: '100%', lineHeight: 1.2 }}
                        >
                            Out of stock
                        </Typography>
                    ) : stockQty != null ? (
                        <Typography
                            variant="caption"
                            color="#212b36"
                            fontSize="11px"
                            sx={{ textAlign: 'right', width: '100%', lineHeight: 1.2 }}
                        >
                            Stock: {stockQty}
                        </Typography>
                    ) : null}
                    {isCompulsory && (
                        <Typography variant="caption" color="text.secondary" fontSize="11px">
                            Compulsory {tabType === 'get' ? 'Get' : 'Buy'}: {product?.compulsory_quantity || 0}
                            {tabType === 'get' && compulsoryQty > perSetCompulsoryQty
                                ? ` (min ${compulsoryQty})`
                                : ''}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Card>
    );
};

export default ProductCard;

